import { Component, OnInit, inject } from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormControl,
	FormGroup,
} from '@angular/forms';
import { openClose } from '@core/animations/animations';
import {
	BusinessUser,
	COMMISSIONER_SELECTION_TYPE,
	DATE_FORMAT,
	SIGNING_LOCATION_TYPE,
	SIGNING_REGION_TYPE,
	WITNESS_SELECTION_TYPE,
} from '@models/businesses';
import { MatterFileType, PropertyStatus } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { dispatch, select, Store } from '@ngxs/store';
import { MatterActions } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { CreateStatusType } from '@state/utils/models';
import {
	addHours,
	addMinutes,
	compareDesc,
	format,
	fromUnixTime,
	parseISO,
} from 'date-fns';
import LogRocket from 'logrocket';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { TimePicker } from 'src/app/shared/atomic/atoms/time-picker/time-picker.component';
import { Iso3166Utils } from '@core/util/iso3166.utils';
import { getFullName } from '@core/util/string';
import {
	LocationTypeDropdownOptions,
	RegionTypeDropdownOptions,
} from 'src/app/private/manage/business-details/address-dialog/address-dialog.component';
import { SegmentedButtonOption } from 'src/app/ui/button-segmented/button-segmented.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { TestHooks } from './key-dates-modal.hooks';

interface IKeyDatesFormGroup {
	agreementDate: FormControl<string>;
	requisitionDate: FormControl<string>;
	occupancyDate: FormControl<string>;
	closingDate: FormControl<string>;
}

interface IAppointmentFormGroup {
	appointmentDate: FormControl<string>;
	appointmentTime: FormControl<TimePicker>;
	executingDocsAtCity: FormControl<string>;
	executingDocsAtProvince: FormControl<string>;
	executingDate: FormControl<string>;
	witnessSelectionType: FormControl<WITNESS_SELECTION_TYPE>;
	witnessUserId: FormControl<string>;
	witnessName: FormControl<string>;
	witnessEmail: FormControl<string>;
	witnessPhoneNumber: FormControl<string>;
	commissionerUserId: FormControl<string>;
	commissionerSelectionType: FormControl<COMMISSIONER_SELECTION_TYPE>;
	commissionerName: FormControl<string>;
	commissionerEmail: FormControl<string>;
	commissionerPhoneNumber: FormControl<string>;
	remoteSigning: FormControl<boolean>;
	officeSigningLocation: FormControl<string>;
	officeSigningLocationType: FormControl<SIGNING_LOCATION_TYPE>;
	officeSigningRegion: FormControl<string>;
	officeSigningRegionType: FormControl<SIGNING_REGION_TYPE>;
	clientSigningLocation: FormControl<string>;
	clientSigningLocationType: FormControl<string>;
	clientSigningRegion: FormControl<string>;
	clientSigningRegionType: FormControl<string>;
}
interface IKeyDatesModalForm {
	keyDates: FormGroup<IKeyDatesFormGroup>;
	appointment: FormGroup<IAppointmentFormGroup>;
}

const CommissionerRadioGroupItems: SegmentedButtonOption[] = [
	{
		label: 'Lawyer',
		id: COMMISSIONER_SELECTION_TYPE.LAWYER,
		intercomAttr: 'commissionerType-Lawyer',
	},
	{
		label: 'Clerk',
		id: COMMISSIONER_SELECTION_TYPE.CLERK,
		intercomAttr: 'commissionerType-Clerk',
	},
	{
		label: 'Other',
		id: COMMISSIONER_SELECTION_TYPE.OTHER,
		intercomAttr: 'commissionerType-Other',
	},
];

const WitnessRadioGroupItems: SegmentedButtonOption[] = [
	{
		label: 'Lawyer',
		id: WITNESS_SELECTION_TYPE.LAWYER,
		intercomAttr: 'witnessType-Lawyer',
	},
	{
		label: 'Clerk',
		id: WITNESS_SELECTION_TYPE.CLERK,
		intercomAttr: 'witnessType-Clerk',
	},
	{
		label: 'Other',
		id: WITNESS_SELECTION_TYPE.OTHER,
		intercomAttr: 'witnessType-Other',
	},
];

@UntilDestroy()
@Component({
	selector: 'app-key-dates-modal',
	templateUrl: './key-dates-modal.component.html',
	styleUrls: ['./key-dates-modal.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class KeyDatesModalComponent implements OnInit {
	testHooks = TestHooks;
	private store = inject(Store);
	private fb = inject(FormBuilder);
	private drawerService = inject(DrawerService);
	private setKeyDates = dispatch(MatterActions.SetKeyDates);
	private matterId_ = select(SelectedMatter.id);
	private matterType_ = select(SelectedMatter.matterFileType);
	private assignedLawyerUser_ = select(SelectedMatter.assignedLawyerUser);
	private assignedClerkUser_ = select(SelectedMatter.assignedClerkUser);
	public submitted = false;
	public CreateStatusType = CreateStatusType;
	public minDate: string;
	public minDateLabel: string;
	public resaleStatus: string = PropertyStatus.RESALE;
	public executingLabel = 'executing city';
	public isNotRefinance: boolean;

	public COMMISSIONER_SELECTION_TYPE = COMMISSIONER_SELECTION_TYPE;
	public WITNESS_SELECTION_TYPE = WITNESS_SELECTION_TYPE;
	public commissionerRadioGroupItems = CommissionerRadioGroupItems;
	public witnessRadioGroupItems = WitnessRadioGroupItems;
	public locationTypeDropdownOptions = LocationTypeDropdownOptions;
	public regionTypeDropdownOptions = RegionTypeDropdownOptions;

	public get showOccupancyDate(): boolean {
		return this.isNotRefinance && this.status === PropertyStatus.NEW;
	}
	private status: PropertyStatus;
	private defaultAppointmentTime = { hours: 9, minutes: 0 }; // 9:00 AM

	public showOfficeSignatureLocationPreview$ = new BehaviorSubject<boolean>(
		false,
	);
	public showClientSignatureLocationPreview$ = new BehaviorSubject<boolean>(
		false,
	);

	createStatus$ = this.store.select(SelectedMatter.getCreateStatus);
	occupancyDate$ = this.store.select(SelectedMatter.occupancyDate);
	closingDate$ = this.store.select(SelectedMatter.closingDate);
	requisitionDate$ = this.store.select(
		SelectedMatter.titleSearchCompletionDate,
	);
	agreementDate$ = this.store.select(SelectedMatter.agreementDate);
	appointment$ = this.store.select(SelectedMatter.appointment);
	propertyType$ = this.store.select(SelectedMatter.propertyType);
	office$ = this.store.select(SelectedMatter.office);

	form = this.fb.group<IKeyDatesModalForm>({
		keyDates: this.fb.group<IKeyDatesFormGroup>({
			agreementDate: this.fb.control(null),
			requisitionDate: this.fb.control(null),
			occupancyDate: this.fb.control(null),
			closingDate: this.fb.control(null),
		}),
		appointment: this.fb.group<IAppointmentFormGroup>({
			appointmentDate: this.fb.control(null),
			appointmentTime: this.fb.control(null),
			executingDocsAtCity: this.fb.control(null),
			executingDocsAtProvince: this.fb.control(null),
			executingDate: this.fb.control(null),
			witnessSelectionType: this.fb.control(WITNESS_SELECTION_TYPE.LAWYER),
			witnessUserId: this.fb.control(null),
			witnessName: this.fb.control(null),
			witnessEmail: this.fb.control(null),
			witnessPhoneNumber: this.fb.control(null),
			commissionerSelectionType: this.fb.control(
				COMMISSIONER_SELECTION_TYPE.LAWYER,
			),
			commissionerUserId: this.fb.control(null),
			commissionerName: this.fb.control(null),
			commissionerEmail: this.fb.control(null),
			commissionerPhoneNumber: this.fb.control(null),
			remoteSigning: this.fb.control(null),

			officeSigningLocation: this.fb.control(null),
			officeSigningLocationType: this.fb.control(null),
			officeSigningRegion: this.fb.control(null),
			officeSigningRegionType: this.fb.control(null),

			clientSigningLocation: this.fb.control(null),
			clientSigningLocationType: this.fb.control(null),
			clientSigningRegion: this.fb.control(null),
			clientSigningRegionType: this.fb.control(null),
		}),
	});

	public get keyDates() {
		return this.form.get('keyDates') as FormGroup<IKeyDatesFormGroup>;
	}
	public get occupancyDate() {
		return this.keyDates.get('occupancyDate');
	}
	public get closingDate() {
		return this.keyDates.get('closingDate');
	}
	public get requisitionDate() {
		return this.keyDates.get('requisitionDate');
	}
	public get agreementDate() {
		return this.keyDates.get('agreementDate');
	}
	public get remoteSigning() {
		return this.form.get('appointment.remoteSigning');
	}
	public get appointmentDate() {
		return this.form.get('appointment.appointmentDate');
	}
	public get appointmentTime() {
		return this.form.get('appointment.appointmentTime');
	}
	public get witnessSelectionType() {
		return this.form.get('appointment.witnessSelectionType');
	}
	public get witnessUserId() {
		return this.form.get('appointment.witnessUserId');
	}
	public get witnessName() {
		return this.form.get('appointment.witnessName');
	}
	public get witnessEmail() {
		return this.form.get('appointment.witnessEmail');
	}
	public get witnessPhoneNumber() {
		return this.form.get('appointment.witnessPhoneNumber');
	}
	public get commissionerSelectionType() {
		return this.form.get('appointment.commissionerSelectionType');
	}
	public get commissionerUserId() {
		return this.form.get('appointment.commissionerUserId');
	}
	public get commissionerName() {
		return this.form.get('appointment.commissionerName');
	}
	public get commissionerEmail() {
		return this.form.get('appointment.commissionerEmail');
	}
	public get commissionerPhoneNumber() {
		return this.form.get('appointment.commissionerPhoneNumber');
	}
	public get officeSigningLocation() {
		return this.form.get('appointment.officeSigningLocation');
	}
	public get officeSigningLocationType() {
		return this.form.get('appointment.officeSigningLocationType');
	}
	public get officeSigningRegion() {
		return this.form.get('appointment.officeSigningRegion');
	}
	public get officeSigningRegionType() {
		return this.form.get('appointment.officeSigningRegionType');
	}
	public get clientSigningLocation() {
		return this.form.get('appointment.clientSigningLocation');
	}
	public get clientSigningLocationType() {
		return this.form.get('appointment.clientSigningLocationType');
	}
	public get clientSigningRegion() {
		return this.form.get('appointment.clientSigningRegion');
	}
	public get clientSigningRegionType() {
		return this.form.get('appointment.clientSigningRegionType');
	}

	ngOnInit(): void {
		this.form.valueChanges
			.pipe(
				untilDestroyed(this),
				tap((formValues) => {
					this.showOfficeSignatureLocationPreview$.next(
						!!formValues.appointment?.officeSigningLocation ||
							!!formValues.appointment?.officeSigningLocationType ||
							!!formValues.appointment?.officeSigningRegion ||
							!!formValues.appointment?.officeSigningRegionType,
					);
				}),
			)
			.subscribe();

		this.form.valueChanges
			.pipe(
				untilDestroyed(this),
				tap((formValues) => {
					this.showClientSignatureLocationPreview$.next(
						formValues.appointment?.remoteSigning &&
							(!!formValues.appointment?.clientSigningLocation ||
								!!formValues.appointment?.clientSigningLocationType ||
								!!formValues.appointment?.clientSigningRegion ||
								!!formValues.appointment?.clientSigningRegionType),
					);
				}),
			)
			.subscribe();

		this.propertyType$.pipe(untilDestroyed(this)).subscribe((_propertyType) => {
			this.status = _propertyType?.status;
		});
		const matterType = this.matterType_();
		this.isNotRefinance = matterType !== MatterFileType.REFINANCE;
		this.executingLabel =
			matterType === MatterFileType.SALE
				? 'Vendor'
				: matterType === MatterFileType.PURCHASE
					? 'Purchaser'
					: 'Mortgagor';
		this.addValidators();
		this.hydrateForm();

		this.createStatus$.pipe(untilDestroyed(this)).subscribe((status) => {
			switch (status) {
				case CreateStatusType.success:
					this.drawerService.closeDrawer();
					break;
				case CreateStatusType.failed:
					LogRocket.error('Error saving key dates');
					break;
			}
		});
	}

	async save() {
		this.submitted = true;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const { keyDates, appointment } = this.form.getRawValue();
		const office = this.store.selectSnapshot(SelectedMatter.office);

		// When remote signing is not selected, the client signing fields will not be applicable so we clear their values
		if (!appointment.remoteSigning) {
			appointment.clientSigningLocationType = null;
			appointment.clientSigningLocation = null;
			appointment.clientSigningRegionType = null;
			appointment.clientSigningRegion = null;
		}

		const officeSigningLocation = office?.signingLocation;
		const officeSigningRegion = office?.signingRegion;
		const officeSigningLocationType = office?.signingLocationType;
		const officeSigningRegionType = office?.signingRegionType;

		// Only clear if ALL fields match office defaults (indicating no user overrides)
		if (
			appointment.officeSigningLocation === officeSigningLocation &&
			appointment.officeSigningRegion === officeSigningRegion &&
			appointment.officeSigningLocationType === officeSigningLocationType &&
			appointment.officeSigningRegionType === officeSigningRegionType
		) {
			appointment.officeSigningLocation = null;
			appointment.officeSigningRegion = null;
			appointment.officeSigningLocationType = null;
			appointment.officeSigningRegionType = null;
		}

		this.setKeyDates({
			matterId: this.matterId_(),
			keyDates,
			appointment: {
				...appointment,
				appointmentTime: this.getAppointmentTime(
					appointment.appointmentDate,
					appointment.appointmentTime,
				),
			},
		});

		this.drawerService.closeDrawer();
	}

	public customWitnessChanged() {
		this.witnessUserId.setValue(null);
	}

	public customCommissionerChanged() {
		this.commissionerUserId.setValue(null);
	}

	private setMinDate(): void {
		const { date, minDateLabel } = [
			{ date: this.appointmentDate?.value, minDateLabel: 'Appointment Date' },
			{ date: this.occupancyDate?.value, minDateLabel: 'Occupancy Date' },
		]
			.filter((d) => d.date)
			.sort((a, b) => compareDesc(parseISO(a.date), parseISO(b.date)))[0] ?? {
			date: null,
			minDateLabel: null,
		};
		this.minDate = date;
		this.minDateLabel = minDateLabel;
	}

	private appointmentTimeRequired(control: AbstractControl) {
		if (this.appointmentDate?.value && !control.value) {
			return { required: true };
		}
		return null;
	}

	private datesDoNotExceedClosingDateValidation(
		keyDates: FormGroup<IKeyDatesFormGroup>,
	) {
		const { agreementDate, requisitionDate, occupancyDate, closingDate } =
			keyDates.getRawValue();
		if (!closingDate) return null;

		if (agreementDate && agreementDate > closingDate) {
			this.agreementDate?.markAsTouched();
			this.agreementDate?.setErrors({ maxDate: { value: 'Closing Date' } });
		} else {
			this.agreementDate?.setErrors(null);
		}

		if (requisitionDate && requisitionDate > closingDate) {
			this.requisitionDate?.markAsTouched();
			this.requisitionDate?.setErrors({ maxDate: { value: 'Closing Date' } });
		} else {
			this.requisitionDate?.setErrors(null);
		}

		if (occupancyDate && occupancyDate > closingDate) {
			this.occupancyDate?.markAsTouched();
			this.occupancyDate?.setErrors({ maxDate: { value: 'Closing Date' } });
		} else {
			this.occupancyDate?.setErrors(null);
		}

		return null;
	}

	private addValidators() {
		this.keyDates.setValidators([
			this.datesDoNotExceedClosingDateValidation.bind(this),
		]);
		this.appointmentTime.setValidators([
			this.appointmentTimeRequired.bind(this),
		]);
	}

	private hydrateForm() {
		this.hydrateOccupancyDate();
		this.hydrateClosingDate();
		this.hydrateRequisitionDate();
		this.hydrateAgreementDate();
		this.hydrateAppointment();
		this.hydrateAppointmentSigningFields();
	}

	private hydrateOccupancyDate() {
		if (this.showOccupancyDate) {
			this.occupancyDate$
				.pipe(
					untilDestroyed(this),
					filter((occupancyDate) => !!occupancyDate),
				)
				.subscribe((_occupancyDate) => {
					if (_occupancyDate < this.agreementDate.value) {
						this.minDate = this.occupancyDate.value;
					}
					this.occupancyDate.setValue(_occupancyDate);
				});
			this.occupancyDate.valueChanges
				.pipe(untilDestroyed(this))
				.subscribe(() => this.setMinDate());
		} else {
			this.keyDates.removeControl('occupancyDate');
		}
	}

	private hydrateClosingDate() {
		this.closingDate$
			.pipe(
				untilDestroyed(this),
				filter((closingDate) => !!closingDate),
			)
			.subscribe((_closingDate) => {
				this.closingDate.setValue(_closingDate);
			});
	}

	private hydrateRequisitionDate() {
		if (this.isNotRefinance) {
			this.requisitionDate$
				.pipe(
					untilDestroyed(this),
					filter((requisitionDate) => !!requisitionDate),
				)
				.subscribe((_requisitionDate) => {
					this.requisitionDate.setValue(_requisitionDate);
				});
		} else {
			this.keyDates.removeControl('requisitionDate');
		}
	}

	private hydrateAgreementDate() {
		this.agreementDate$
			.pipe(
				untilDestroyed(this),
				filter((agreementDate) => !!agreementDate),
			)
			.subscribe((_agreementDate) => {
				this.agreementDate.setValue(_agreementDate);
			});
	}

	private hydrateAppointment() {
		this.appointment$
			.pipe(
				untilDestroyed(this),
				filter((appointment) => !!appointment),
				take(1),
			)
			.subscribe((appointment) => {
				if (appointment.witnessSelectionType) {
					this.witnessSelectionType.setValue(appointment.witnessSelectionType);
				}
				if (appointment.witnessUserId) {
					this.witnessUserId.setValue(appointment.witnessUserId);
				}
				if (appointment.witnessName) {
					this.witnessName.setValue(appointment.witnessName);
				}
				if (appointment.witnessPhoneNumber) {
					this.witnessPhoneNumber.setValue(appointment.witnessPhoneNumber);
				}
				if (appointment.witnessEmail) {
					this.witnessEmail.setValue(appointment.witnessEmail);
				}
				if (appointment.commissionerSelectionType) {
					this.commissionerSelectionType.setValue(
						appointment.commissionerSelectionType,
					);
				}
				if (appointment.commissionerUserId) {
					this.commissionerUserId.setValue(appointment.commissionerUserId);
				}
				if (appointment.commissionerName) {
					this.commissionerName.setValue(appointment.commissionerName);
				}
				if (appointment.commissionerEmail) {
					this.commissionerEmail.setValue(appointment.commissionerEmail);
				}
				if (appointment.commissionerPhoneNumber) {
					this.commissionerPhoneNumber.setValue(
						appointment.commissionerPhoneNumber,
					);
				}
				if (appointment.remoteSigning) {
					this.remoteSigning.setValue(appointment.remoteSigning);
				}

				if (appointment.appointmentTime) {
					const date = fromUnixTime(appointment.appointmentTime / 1000);
					this.appointmentDate.setValue(format(date, DATE_FORMAT));
					this.appointmentTime.setValue({
						hours: date.getHours(),
						minutes: date.getMinutes(),
					});
				}
			});

		this.appointmentDate.valueChanges
			.pipe(untilDestroyed(this))
			.subscribe((value) => {
				if (!value) {
					this.appointmentTime.setValue(null);
				} else if (!this.appointmentTime.value) {
					this.appointmentTime.setValue(this.defaultAppointmentTime);
				}
				this.setMinDate();
			});
	}

	private hydrateAppointmentSigningFields() {
		combineLatest([this.appointment$, this.office$])
			.pipe(
				untilDestroyed(this),
				filter(([appointment, office]) => !!appointment && !!office),
				take(1),
			)
			.subscribe(([appointment, office]) => {
				if (
					appointment.officeSigningLocation ||
					office.signingLocation ||
					office.city
				) {
					this.officeSigningLocation.setValue(
						appointment.officeSigningLocation ||
							office.signingLocation ||
							office.city,
					);
				}

				this.officeSigningLocationType.setValue(
					appointment.officeSigningLocationType ||
						office.signingLocationType ||
						SIGNING_LOCATION_TYPE.CITY,
				);

				const isoRegionName = Iso3166Utils.getRegionName(
					office?.province,
					office?.country,
				);
				if (
					appointment.officeSigningRegion ||
					office.signingRegion ||
					isoRegionName
				) {
					this.officeSigningRegion.setValue(
						appointment.officeSigningRegion ||
							office.signingRegion ||
							isoRegionName,
					);
				}

				this.officeSigningRegionType.setValue(
					appointment.officeSigningRegionType ||
						office.signingRegionType ||
						SIGNING_REGION_TYPE.PROVINCE,
				);

				if (appointment.clientSigningLocation) {
					this.clientSigningLocation.setValue(
						appointment.clientSigningLocation,
					);
				}
				if (appointment.clientSigningLocationType) {
					this.clientSigningLocationType.setValue(
						appointment.clientSigningLocationType,
					);
				}
				if (appointment.clientSigningRegion) {
					this.clientSigningRegion.setValue(appointment.clientSigningRegion);
				}
				if (appointment.clientSigningRegionType) {
					this.clientSigningRegionType.setValue(
						appointment.clientSigningRegionType,
					);
				}
			});
	}

	private getAppointmentTime(
		appointmentDate: string,
		appointmentTime: TimePicker,
	): number {
		if (!appointmentDate || !appointmentTime) return null;

		let _date = parseISO(this.appointmentDate.value);
		_date = addHours(_date, this.appointmentTime.value.hours);
		_date = addMinutes(_date, this.appointmentTime.value.minutes);
		return _date.getTime();
	}

	public setOfficialsSelectionType<
		Official extends 'commissioner' | 'witness',
		SelectionType extends Official extends 'commissioner'
			? COMMISSIONER_SELECTION_TYPE
			: WITNESS_SELECTION_TYPE,
	>(type: Official, selectionType: SelectionType) {
		switch (type) {
			case 'commissioner': {
				this.commissionerSelectionType.setValue(
					selectionType as COMMISSIONER_SELECTION_TYPE,
				);
				if (COMMISSIONER_SELECTION_TYPE.LAWYER === selectionType) {
					this.setCommissionerUser(this.assignedLawyerUser_());
				} else if (COMMISSIONER_SELECTION_TYPE.CLERK === selectionType) {
					this.setCommissionerUser(this.assignedClerkUser_());
				}
				break;
			}
			case 'witness': {
				this.witnessSelectionType.setValue(
					selectionType as WITNESS_SELECTION_TYPE,
				);
				if (WITNESS_SELECTION_TYPE.LAWYER === selectionType) {
					this.setWitnessUser(this.assignedLawyerUser_());
				} else if (WITNESS_SELECTION_TYPE.CLERK === selectionType) {
					this.setWitnessUser(this.assignedClerkUser_());
				}
				break;
			}
			default:
				throw new Error(`Unknown official type: ${type}`);
		}
	}

	public setWitnessUser(user: BusinessUser): void {
		this.witnessUserId.setValue(user.id);
		this.witnessEmail.setValue(user.email ?? '');
		this.witnessName.setValue(getFullName([user.firstName, user.lastName]));
		this.witnessPhoneNumber.setValue(user.phoneNumber ?? '');
	}

	public setCommissionerUser(user: BusinessUser): void {
		this.commissionerUserId.setValue(user.id);
		this.commissionerEmail.setValue(user.email ?? '');
		this.commissionerName.setValue(
			getFullName([user.firstName, user.lastName]),
		);
		this.commissionerPhoneNumber.setValue(user.phoneNumber ?? '');
	}
}
